import { signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { WidgetState } from './widget.state';
import { inject } from '@angular/core';
import { WidgetHelperService } from '../../services/widget/widget-helper.service';
import {
    WidgetConfig,
    WidgetType,
    ScatterData,
    RegressionData,
} from '../../services/grid-widget-config.service';
import { ChartDataset } from 'chart.js';
import { ComparisonValue } from '../../models/geo-data';
import { ComparisonValueStore } from '../comparison-value/comparison-value.state';
import { normalize } from '../../utility/chart.utility';

/**
 * Feature providing axis-specific chart methods (scatter, bubble, regression)
 * and comparison dataset logic.
 */
export function withChartAxisMethods() {
    return signalStoreFeature(
        {
            state: type<WidgetState>(),
        },
        withMethods(
            (
                store,
                helper = inject(WidgetHelperService),
                comparisonStore = inject(ComparisonValueStore)
            ) => {
                // Destructure methods from store (metadata feature must be composed before this)
                const { getDatasetLabel, dataSetProperty } = store as any;

                /**
                 * Calculates and generates an array of scatter plot data from the given inputs.
                 */
                const calculateScatterData = (
                    xs: number[],
                    ys: number[],
                    rs: number[] | null,
                    labels?: string[]
                ): ScatterData[] => {
                    let minR: number | undefined;
                    let maxR: number | undefined;

                    if (rs) {
                        minR = Math.min(...rs);
                        maxR = Math.max(...rs);
                    }

                    return xs
                        .map((x: number, i: number) => {
                            const rawR = rs && rs[i] ? rs[i] : 1;
                            const scaledR =
                                rs && maxR !== undefined && minR !== undefined
                                    ? normalize(rawR, minR, maxR, 1, 20)
                                    : 1;

                            return {
                                x,
                                y: ys[i],
                                r: scaledR,
                                rRaw: rs?.[i] || undefined,
                                label: labels?.[i],
                            };
                        })
                        .filter(
                            (data: ScatterData) =>
                                data.x !== undefined && data.y !== undefined
                        );
                };

                /**
                 * Converts grouped data into axis data for a chart, such as scatter or bubble chart data.
                 */
                const convertToAxisData = (
                    groupedData: Record<string, Record<string, number | null>>,
                    config: WidgetConfig,
                    type: WidgetType,
                    comparisonValues: Record<string, ComparisonValue>
                ): ChartDataset[] | undefined => {
                    const currentType = type || 'adder';
                    const ids = config?.axisProperties.length
                        ? [...config.axisProperties]
                        : [...Object.keys(groupedData).slice(0, 2)];

                    if (currentType === 'bubble') {
                        const radiusValue = Object.keys(groupedData)[2];
                        if (radiusValue) ids.push(radiusValue);
                    }

                    if (!config || !groupedData || !currentType) {
                        return;
                    }

                    if (
                        !ids ||
                        (ids.length < 2 && currentType === 'scatter') ||
                        (ids.length < 3 && currentType === 'bubble')
                    ) {
                        return;
                    }

                    const radiusProperty =
                        config?.radiusProperty || Object.keys(groupedData)[2] || null;
                    if (radiusProperty) {
                        ids.push(radiusProperty);
                    }

                    let label = 'x: ';
                    const chartData: ChartDataset = {
                        data: [],
                        backgroundColor: helper.getChartBackgroundColor(
                            currentType,
                            config,
                            0
                        ),
                        borderColor: helper.getChartBackgroundColor(
                            currentType,
                            config,
                            0
                        ),
                        label: '',
                        pointRadius: config.dotSize || 4,
                    };

                    const dataItems: any = ids.map((id, idx) => {
                        const dataLabel = getDatasetLabel(
                            config,
                            dataSetProperty(config),
                            id,
                            true,
                            comparisonValues
                        );
                        if (idx === 1) {
                            label += ' | y: ';
                            config.xAxisLabel = config.axis?.x.label || dataLabel;
                        } else if (idx === 0) {
                            config.yAxisLabel = config.axis?.y.label || dataLabel;
                        }

                        label += dataLabel;

                        return groupedData[id];
                    });

                    if (
                        !dataItems ||
                        !dataItems[0] ||
                        !dataItems[1] ||
                        dataItems.length < 2
                    ) {
                        return;
                    }

                    // Align all axes by point key (feature/data layer/indicator),
                    // so every point keeps its identity for the tooltip title.
                    const pointKeys: string[] = Object.keys(dataItems[0]);
                    const xs: number[] = pointKeys.map(key => dataItems[0][key]);
                    const ys: number[] = pointKeys.map(key => dataItems[1][key]);
                    const rs: number[] | null = dataItems[2]
                        ? pointKeys.map(key => dataItems[2][key])
                        : null;
                    const pointLabels: string[] = pointKeys.map(key =>
                        getDatasetLabel(
                            config,
                            config.groupProperty,
                            key,
                            false,
                            comparisonValues
                        )
                    );

                    const converted: ScatterData[] = calculateScatterData(
                        xs,
                        ys,
                        rs,
                        pointLabels
                    ).filter(data => data.x !== undefined && data.y !== undefined);

                    chartData.data = converted;
                    chartData.label = label;

                    if (config.displayRegression) {
                        const regressionData: RegressionData =
                            helper.calcRegression(converted);
                        const regressionDataset = {
                            regression: true,
                            label: 'r2: ' + regressionData.r2,
                            customTooltip: 'r2: ' + regressionData.r2,
                            r2: regressionData.r2,
                            data: regressionData.line,
                            type: 'line',
                            borderColor: 'rgba(255, 99, 132, 1)',
                            borderWidth: 2,
                            fill: false,
                        } as any;

                        return [chartData, regressionDataset];
                    } else {
                        return [chartData];
                    }
                };

                /**
                 * Adds comparison datasets to the main chart datasets.
                 */
                const addComparisonDatasets = (
                    datasets: any[],
                    config: WidgetConfig,
                    type: WidgetType,
                    comparisonData: any,
                    featureData: any
                ): any[] => {
                    if (
                        type !== 'bar' ||
                        !config.comparisonValues ||
                        !config.comparisonValues.length
                    ) {
                        return datasets;
                    }

                    datasets = datasets.map((dataset: any) => {
                        dataset.type = 'bar';
                        return dataset;
                    });

                    const compSets: any[] = [];
                    config.comparisonValues.forEach((compKey, compIndex) => {
                        const data = comparisonData[compKey];
                        if (
                            data &&
                            (config.groupProperty === 'feature' ||
                                config.focusProperty === 'feature')
                        ) {
                            Object.values(data).forEach((item: any, idx) => {
                                const labelKey = Object.keys(featureData)[idx];
                                const color = helper.getChartBackgroundColor(
                                    type,
                                    config,
                                    idx
                                );
                                compSets.push(
                                    helper.getComparisonLineOptions(
                                        Object.values(item),
                                        Object.keys(item),
                                        {
                                            color,
                                            compIndex,
                                            legendIndex: idx,
                                            labelKey: labelKey,
                                            tooltip: comparisonStore.getNameByKey(compKey),
                                            asBar: !datasets.length,
                                        }
                                    )
                                );
                            });
                        } else if (data) {
                            compSets.push(
                                helper.getComparisonLineOptions(
                                    Object.values(data),
                                    Object.keys(data),
                                    {
                                        color: 'red',
                                        tooltip: comparisonStore.getNameByKey(compKey),
                                        asBar: !datasets.length,
                                    }
                                )
                            );
                        }
                    });

                    return [...datasets, ...compSets];
                };

                return {
                    calculateScatterData,
                    convertToAxisData,
                    addComparisonDatasets,
                };
            }
        )
    );
}
